import { ReactNode, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import ExpandableSection from "@components/animations/ExpandableSection";
import PrimaryButton from "@components/buttons/PrimaryButton";
import ArticleCard from "@components/cards/ArticleCard";
import BasicCard from "@components/cards/BasicCard";
import styles from "@components/carePlan/CarePlan.module.css";
import Tool from "@components/carePlan/Tool";
import DateStamp from "@components/dates/DateStamp";
import EmployerBenefitCard from "@components/explore/EmployerBenefitCard";
import FileResource from "@components/FileResource";
import Section from "@components/layout/Section";
import LegalDocumentTracker from "@components/LegalDocumentTracker";
import MedicationTracker from "@components/MedicationTracker";
import PinToggle from "@components/PinToggle";
import ProviderTracker from "@components/ProviderTracker";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import { JourneyStatusEnum } from "@typing/Enums";
import {
  AppEventActionKindEnum,
  AppEventCreateDocument,
  AppEventCreateMutation,
  AppEventCreateMutationVariables,
  CarePlanActionItemCtaEnum,
  GetJourneyWithPlanQuery,
  GetPinsQuery,
  PinResourceTypeEnum
} from "@typing/Generated";
import { medicationsBatched, providersBatched } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Journey = GetJourneyWithPlanQuery["journey"];
type PinResource = GetPinsQuery["pins"][0]["resource"];

type Props = {
  journey: Journey;
  pins: GetPinsQuery["pins"];
};

type PinGroupProps = {
  children: ReactNode;
  items: unknown[];
  titleKey: string;
};

const PinGroup = ({ children, items, titleKey }: PinGroupProps) => {
  const opener = useOpener();

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={styles.pinSection}>
      <ButtonLink data-test="pin-group-link" onClick={opener.toggle} stylized={false}>
        <div className={styles.pinGroup}>
          <BodyCopy bold color="primary">
            <FormattedMessage id={titleKey} />
          </BodyCopy>
          <Icon color="primary" icon={opener.isOpen ? "chevronUp" : "chevronDown"} />
        </div>
      </ButtonLink>
      <div className={styles.containerWidth}>
        <ExpandableSection isOpen={opener.isOpen} key={titleKey}>
          <div>{children}</div>
        </ExpandableSection>
      </div>
    </div>
  );
};

const CarePlanPinned = ({ journey, pins }: Props) => {
  const [selectedBenefitId, setSelectedBenefitId] = useState<string | null>(null);

  const [createAppEvent] = useMutationWithValidation<AppEventCreateMutation, AppEventCreateMutationVariables>(
    AppEventCreateDocument,
    "appEvent"
  );

  const generateHandleEmployerBenefitClick = (employerBenefitId: string) => () => {
    if (selectedBenefitId === employerBenefitId) {
      setSelectedBenefitId(null);
    } else {
      setSelectedBenefitId(employerBenefitId);
      createAppEvent({
        actionKind: AppEventActionKindEnum.employer_benefit_opened,
        journeyId: journey.id,
        resourceId: employerBenefitId
      });
    }
  };

  const pinsGrouped = useMemo(
    () => ({
      articles: pins
        .filter(pin => pin.resource?.__typename === "Article")
        .map(pin => pin.resource as Extract<PinResource, { __typename: "Article" }>),
      employerBenefits: pins
        .filter(pin => pin.resource?.__typename === "EmployerBenefit")
        .map(pin => pin.resource as Extract<PinResource, { __typename: "EmployerBenefit" }>),
      fileResource: pins
        .filter(pin => pin.resource?.__typename === "FileResource")
        .map(pin => pin.resource as Extract<PinResource, { __typename: "FileResource" }>),
      resources: pins
        .filter(pin => pin.resource?.__typename === "Resource")
        .map(pin => pin.resource as Extract<PinResource, { __typename: "Resource" }>),
      tools: pins.filter(pin => pin.tool).map(pin => pin.tool as CarePlanActionItemCtaEnum),
      worksheets: pins
        .filter(pin => pin.resource?.__typename === "Worksheet")
        .map(pin => pin.resource as Extract<PinResource, { __typename: "Worksheet" }>)
    }),
    [pins]
  );

  const legalOpener = useOpener();
  const medicationTrackerOpener = useOpener();
  const providerOpener = useOpener();
  const myClient = useMyClient();

  const batchedProviders = useMemo(() => providersBatched(journey.providers), [journey.providers]);
  const batchedMedications = useMemo(() => medicationsBatched(journey.medications), [journey.medications]);

  return (
    <Section
      color="primary-300"
      descriptionKey="pages.carePlan.base.pins.description"
      icon="thumbtack"
      titleKey="pages.carePlan.base.pins.title"
    >
      <PinGroup items={pinsGrouped.resources} titleKey="pages.carePlan.base.pins.titles.resources">
        <div className={styles.pinResources}>
          {pinsGrouped.resources.map(resource => (
            <BasicCard hover="none" key={resource.id}>
              <div className={styles.pinResource} data-test="pinned-resource">
                <div className={styles.pinResourceTitle}>
                  <BodyCopy color="primary">{resource.resourceTitle}</BodyCopy>
                </div>

                <div className={styles.pinResourceControls}>
                  <PinToggle
                    color="gray-500"
                    journeyId={journey.id}
                    resourceId={resource.id}
                    resourceType={PinResourceTypeEnum.RESOURCE}
                  />
                </div>
              </div>
              <div className={styles.pinResourceView}>
                <PrimaryButton
                  data-test="pinned-resource-view-details"
                  routerLink={journeyRoute({
                    journeyId: journey.id,
                    linkAfterJourney: `/client-visible-tasks/${resource.task.id}`,
                    myClient
                  })}
                  textKey="pages.carePlan.base.pins.viewDetails"
                />
              </div>
            </BasicCard>
          ))}
        </div>
      </PinGroup>
      <PinGroup items={pinsGrouped.articles} titleKey="pages.carePlan.base.pins.titles.articles">
        <div className={styles.pinResources}>
          {pinsGrouped.articles.map(article => (
            <ArticleCard
              article={{ ...article, title: article.articleTitle }}
              journeyId={journey.id}
              key={article.id}
            />
          ))}
        </div>
      </PinGroup>
      <PinGroup items={pinsGrouped.employerBenefits} titleKey="pages.carePlan.base.pins.titles.employerBenefits">
        <div className={styles.pinResources}>
          {pinsGrouped.employerBenefits.map(employerBenefit => (
            <div key={employerBenefit.id} onClick={generateHandleEmployerBenefitClick(employerBenefit.id)}>
              <div className={styles.articleCardSpacing} key={employerBenefit.id}>
                <EmployerBenefitCard
                  employerBenefit={employerBenefit}
                  expanded={selectedBenefitId === employerBenefit.id}
                  journeyId={journey.id}
                  layout="vertical"
                />
              </div>
            </div>
          ))}
        </div>
      </PinGroup>
      <PinGroup
        items={[...pinsGrouped.tools, ...pinsGrouped.worksheets]}
        titleKey="pages.carePlan.base.pins.titles.tools"
      >
        <div className={styles.pinnedTools}>
          {pinsGrouped.worksheets.map(worksheet => (
            <Tool
              archived={false}
              journeyId={journey.id}
              key={worksheet.id}
              legalOpener={legalOpener}
              medicationTrackerOpener={medicationTrackerOpener}
              providerOpener={providerOpener}
              tool={{
                status: JourneyStatusEnum.COMPLETED,
                worksheet: { ...worksheet, title: worksheet.worksheetTitle }
              }}
            />
          ))}
          {pinsGrouped.tools.map(tool => (
            <Tool
              archived={false}
              journeyId={journey.id}
              key={tool}
              legalOpener={legalOpener}
              medicationTrackerOpener={medicationTrackerOpener}
              providerOpener={providerOpener}
              tool={{
                status: JourneyStatusEnum.COMPLETED,
                type: tool
              }}
            />
          ))}
        </div>
      </PinGroup>
      <PinGroup items={pinsGrouped.fileResource} titleKey="pages.carePlan.base.pins.titles.fileResources">
        <div className={styles.pinResources}>
          {pinsGrouped.fileResource.map(fileResource => (
            <BasicCard hover="none" key={fileResource.id}>
              <div className={styles.fileResource}>
                <div className={styles.fileResourceTitle}>
                  <FileResource fileResource={{ ...fileResource, title: fileResource.fileResourceTitle }} />
                </div>

                <div className={styles.pinResourceControls}>
                  <PinToggle
                    color="gray-500"
                    journeyId={journey.id}
                    resourceId={fileResource.id}
                    resourceType={PinResourceTypeEnum.FILERESOURCE}
                  />
                </div>
              </div>
              <div className={styles.pinResourceView}>
                <DateStamp value={fileResource.updatedAt} />
              </div>
            </BasicCard>
          ))}
        </div>
      </PinGroup>
      <LegalDocumentTracker journey={journey} legalDocumentsOpener={legalOpener} />
      <ProviderTracker
        batchedProviders={batchedProviders}
        journey={journey}
        providerTrackerOpener={providerOpener}
        restartWizard
      />
      <MedicationTracker
        batchedMedications={batchedMedications}
        journey={journey}
        medicationTrackerOpener={medicationTrackerOpener}
        restartWizard
      />
    </Section>
  );
};

export default CarePlanPinned;
